import { useState, useEffect } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  Timestamp,
  setDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import Cache from "../utils/cache";
import { v4 as uuidv4 } from "uuid";
import Select from "react-select";

const MAX_OFFERS_PER_DOC = 600;

const comboRules = [
  { keywords: ["anker", "power"], category: "Power Backup" },
  { keywords: ["men", "jogger"], category: "Men's Clothing" },
  { keywords: ["men", "sweater"], category: "Men's Clothing" },
  { keywords: ["men", "shirt"], category: "Men's Clothing" },
  { keywords: ["men", "polo"], category: "Men's Clothing" },
  { keywords: ["women", "jeans"], category: "Women's Clothing" },
  { keywords: ["women", "pant"], category: "Women's Clothing" },
  { keywords: ["women", "dress"], category: "Women's Clothing" },
  { keywords: ["women", "sweater"], category: "Women's Clothing" },
  { keywords: ["women", "jacket"], category: "Women's Clothing" },
  { keywords: ["women", "blouse"], category: "Women's Clothing" },
  { keywords: ["women", "skirt"], category: "Women's Clothing" },
  { keywords: ["kids", "jogger"], category: "Kids Clothing" },
  { keywords: ["toddler", "dress"], category: "Kids Clothing" },
  { keywords: ["girl", "socks"], category: "Kids Clothing" },
  { keywords: ["boy", "socks"], category: "Kids Clothing" },
  { keywords: ["ring", "doorbell"], category: "Home Security" },
  // Add more combo rules here...
];

const keywordCategoryMap = {
  drones: "Drones",
  drone: "Drones",
  earbuds: "Earbuds",
  garage: "Garage",
  freezer: "Freezer",
  eufy: "Home Security",
  "standing desk": "Office Supplies",
  "trifold wallet": "Men's Lifestyle",
  "security camera": "Home Security",
  "knife": "Kitchen",
  dishwasher: "Kitchen Appliances",
  oven: "Kitchen Appliances",
  toaster: "Kitchen Appliances",
  microwave: "Kitchen Appliances",
  cooktop: "Kitchen Appliances",
  burner: "Kitchen Appliances",
  expresso: "Kitchen Appliances",
  "food processor": "Kitchen Appliances",
  cooker: "Kitchen Appliances",
  juicer: "Kitchen Appliances",
  espresso: "Kitchen Appliances",
  toothbrush: "Personal Care",
  "shop vacuum": "Tools",
  "wet/dry": "Tools",
  vacuum: "Home Appliances",
  mop: "Home Appliances",
  pantry: "Home",
  "floor cleaner": "Home Appliances",
  "air purifier": "Home Appliances",
  heater: "Home Appliances",
  carpet: "Home Appliances",
  cartridge: "Printers",
  toy: "Toys",
  playset: "Toys",
  scooter: "Scooters",
  shower: "Bathroom",
  cookware: "Kitchenware",
  luggage: "Luggage",
  samsonite: "Luggage",
  printer: "Printers",
  kobalt: "Tools",
  screwdriver: "Tools",
  pneumatic: "Tools",
  dewalt: "Tools",
  rug: "Rugs",
  "smart lock": "Home",
  "impact driver": "Tools",
  blower: "Tools",
  craftsman: "Tools",
  "reciprocating saw": "Tools",
  "impact wrench": "Tools",
  "skil pwr": "Tools",
  "sander": "Tools",
  ryobi: "Tools",
  comforter: "Bedding",
  pillow: "Bedding",
  "bed sheet": "Bedding",
  "sheet set": "Bedding",
  shoe: "Footwear",
  sneaker: "Footwear",
  clog: "Footwear",
  watch: "Watches",
  toothpaste: "Personal Care",
  "body wash": "Personal Care",
  hair: "Personal Care",
  sandal: "Footwear",
  backpack: "Bags",
  washer: "Laundry",
  laundry: "Laundry",
  "finish nailer": "Tools",
  laptop: "Laptop",
  macbook: "Laptop",
  case: "Phone Cases",
  bathroom: "Bathroom",
  vanity: "Bathroom",
  "hair dryer": "Beauty",
  "ring battery": "Home Security",
  "outdoor cam": "Home Security",
  camera: "Home Security",
  "action camera": "Cameras",
  outdoor: "Outdoor",
  patio: "Outdoor",
  "remote start": "Car Accessories",
  "solar generator": "Power Backup",
  "car charger": "Car Accessories",
  cam: "Home Security",
  monitor: "Computer",
  desktop: "Computer",
  keyboard: "Computer",
  game: "Games",
  gaming: "Gaming",
  xbox: "Gaming",
  refrigerator: "Kitchen Appliances",
  blender: "Kitchen Appliances",
  nailer: "Tools",
  "tool set": "Tools",
  "ridgid": "Tools",
  "milwaukee": "Tools",
  "toolbox": "Tools",
  "brushless": "Tools",
  mattress: "Mattress",
  "pressure washer": "Tools",
  plant: "Plants",
  inflator: "Tools",
  edger: "Tools",
  drill: "Tools",
  laser: "Tools",
  bag: "Bags",
  phone: "Phone",
  tv: "TV",
  "air fryer": "Kitchen Appliances",
  "dash camera": "DashCams",
  "dashcamera": "DashCams",
  "dash cam": "DashCams",
  "dashcam": "DashCams",
  gps: "GPS",
  charger: "Chargers",
  tumbler: "Drinkware",
  nutribullet: "Kitchen Appliances",
  furniture: "Furniture",
  sofa: "Furniture",
  recliner: "Furniture",
  table: "Furniture",
  // Add as many as you want
};

export default function AddOffer({ uploadedBy }) {
  const getTodayDateString = () => {
    const now = new Date();

    // Convert to Central Time date string using toLocaleString
    const ctDateString = now.toLocaleString("en-US", {
      timeZone: "America/Chicago",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });

    // ctDateString will be like MM/DD/YYYY, so parse it
    const [month, day, year] = ctDateString.split("/");

    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  };


  const initialState = {
    vendor: "",
    productName: "",
    description: "",
    category: "",
    offerImage: "",
    offerLink: "",
    originalPrice: "",
    offerPrice: "",
    rebateAmount: "",
    cashback: "",
    productSKU: "",
    productUPC: "",
    instructions: "",
    freeShippingMinimum: "",
    freeStorePickup: "No",
    tags: "",
    promoCode: "",
    startDttm: getTodayDateString(),  // <-- set default today
    endDttm: "",
    offerType: "",
  };


  const [offer, setOffer] = useState(initialState);
  const [status, setStatus] = useState("");
  const [categories, setCategories] = useState([]);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const vendorOptions = vendors.map((v) => ({ value: v, label: v }));
  const [offerTypeRules, setOfferTypeRules] = useState([]);


  useEffect(() => {
    const loadVendorsAndCategories = async () => {
      let vendorList = [];
      const cached = Cache.getData();
      const cachedVendors = cached?.vendors;

      if (
        cachedVendors &&
        Array.isArray(cachedVendors) &&
        cachedVendors.length > 0 &&
        typeof cachedVendors[0] === "object"
      ) {
        vendorList = cachedVendors.map((v) => v.vendorName || v.id).filter(Boolean);
      } else {
        try {
          const snapshot = await getDocs(collection(db, "vendors"));
          const groupedDocs = snapshot.docs.filter((doc) =>
            doc.id.startsWith("vendors_")
          );

          const allVendors = [];
          groupedDocs.forEach((doc) => {
            const data = doc.data();
            if (Array.isArray(data.vendors)) {
              data.vendors.forEach((vendor) => {
                if (!vendor.vendorName && vendor.id) {
                  vendor.vendorName = vendor.id;
                }
                allVendors.push(vendor);
              });
            }
          });

          vendorList = allVendors.map((v) => v.vendorName || v.id).filter(Boolean);

          Cache.setData({
            ...cached,
            vendors: allVendors,
          });
        } catch (error) {
          console.error("Error loading vendors:", error);
        }
      }

      setVendors(vendorList.sort((a, b) => a.localeCompare(b)));

      try {
        const snapshot = await getDocs(collection(db, "category"));
        const values = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          if (Array.isArray(data.categories)) {
            values.push(...data.categories);
          }
        });

        const sortedCategories = Array.from(new Set(values)).sort((a, b) =>
          a.localeCompare(b) // alphabetical order
        );

        setCategories(sortedCategories);
      } catch (err) {
        console.error("Error loading categories:", err);
      }
    };

    loadVendorsAndCategories();


    const loadPromoCodes = async () => {
      const cached = Cache.getData();
      if (cached.promoCodes && cached.promoCodes.length > 0) {
        return cached.promoCodes;
      }

      try {
        const snapshot = await getDocs(collection(db, "promo_codes"));
        const allPromos = [];

        snapshot.forEach((doc) => {
          const data = doc.data();
          if (Array.isArray(data.promos)) {
            allPromos.push(...data.promos);
          }
        });

        Cache.setData({ ...cached, promoCodes: allPromos });
        return allPromos;
      } catch (err) {
        console.error("Error loading promo codes:", err);
        return [];
      }
    };

    loadPromoCodes();

    const fetchOfferTypes = async () => {
      const querySnapshot = await getDocs(collection(db, "offer_types"));
      const allOffers = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.offers && Array.isArray(data.offers)) {
          allOffers.push(...data.offers);
        }
      });

      setOfferTypeRules(allOffers);
    };

    fetchOfferTypes();

  }, []);

  const isActiveOffer = (offer) => {
    if (!offer.endDttm?.toDate) return false;
    const now = new Date();
    return offer.endDttm.toDate() > now;
  };

  const isSameOffer = (a, b) => {
    const sameVendor = a.vendor?.toLowerCase() === b.vendor?.toLowerCase();
    const sameSKU = a.productSKU && b.productSKU && a.productSKU.toLowerCase() === b.productSKU.toLowerCase();
    const sameName = a.productName && b.productName && a.productName.trim().toLowerCase() === b.productName.trim().toLowerCase();
    const sameDescription = (a.description || "").trim().toLowerCase() === (b.description || "").trim().toLowerCase();
    const samePrice = parseFloat(a.offerPrice) === parseFloat(b.offerPrice);
    return sameVendor && (sameSKU || sameName) && sameDescription && samePrice;
  };

  const isSameButPriceDifferent = (a, b) => {
    const sameVendor = a.vendor?.toLowerCase() === b.vendor?.toLowerCase();
    const sameSKU = a.productSKU && b.productSKU && a.productSKU.toLowerCase() === b.productSKU.toLowerCase();
    const sameName = a.productName && b.productName && a.productName.trim().toLowerCase() === b.productName.trim().toLowerCase();
    const sameDescription = (a.description || "").trim().toLowerCase() === (b.description || "").trim().toLowerCase();
    const priceChanged = parseFloat(a.offerPrice) !== parseFloat(b.offerPrice);
    return sameVendor && (sameSKU || sameName) && sameDescription && priceChanged;
  };

  const addProductOffer = async (newOffer) => {
    const colRef = collection(db, "product_offers");
    const snapshot = await getDocs(colRef);

    const enrichedOffer = {
      ...newOffer,
      offerId: uuidv4(),
    };

    // First check for duplicates or price updates in ALL docs
    for (const docSnap of snapshot.docs) {
      const docData = docSnap.data();
      let offers = Array.isArray(docData.offers) ? docData.offers : [];

      for (let i = 0; i < offers.length; i++) {
        const existing = offers[i];
        if (!isActiveOffer(existing)) continue;

        if (isSameOffer(existing, enrichedOffer)) {
          return "skipped";
        }

        if (isSameButPriceDifferent(existing, enrichedOffer)) {
          offers[i] = {
            ...existing,
            offerPrice: enrichedOffer.offerPrice,
            uploadedDttm: Timestamp.now(),
          };
          await updateDoc(docSnap.ref, { offers });
          return "updated";
        }
      }
    }

    // Try to add to an existing doc that has space
    for (const docSnap of snapshot.docs) {
      const docData = docSnap.data();
      const offers = Array.isArray(docData.offers) ? docData.offers : [];
      const count = docData.offerCount || offers.length;

      if (count < MAX_OFFERS_PER_DOC) {
        offers.push(enrichedOffer);
        await updateDoc(docSnap.ref, {
          offers,
          offerCount: offers.length,
        });
        return "added";
      }
    }

    // If no doc has space, create a new one
    const newDocRef = doc(colRef);
    await setDoc(newDocRef, {
      offers: [enrichedOffer],
      offerCount: 1,
    });

    return "added";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedOffer = { ...offer, [name]: value };

    // --- Existing cashback logic ---
    if (name === "cashback" && Number(value) > 0) {
      if (offer.vendor === "Walmart") {
        updatedOffer.instructions =
          "How to Claim Walmart Cash:\n" +
          "1. Sign in to the Walmart app or website.\n" +
          "2. Go to the product page.\n" +
          "3. Activate the Walmart Cash offer.\n" +
          "4. Shop eligible items online or in-store using Walmart Pay.\n" +
          "5. Rewards appear as pending, then available after the return window.\n" +
          "6. Redeem Walmart Cash at checkout online or in-store.";
        updatedOffer.offerType = "Walmart Cash";
        const endDate = new Date();
        endDate.setDate(endDate.getDate() + 1);
        updatedOffer.endDttm = endDate.toISOString().split("T")[0]; // YYYY-MM-DD
      } else if (offer.vendor === "CVS Pharmacy") {
        updatedOffer.instructions =
          "How to Claim CVS ExtraCare Rewards:\n" +
          "1. Sign in to the CVS app or CVS.com with your ExtraCare account.\n" +
          "2. Go to the product page.\n" +
          "3. Activate the ExtraCare reward offer.\n" +
          "4. Shop eligible items online or in-store and enter your ExtraCare card/phone number at checkout.\n" +
          "5. Rewards will be credited to your ExtraCare account after purchase (Within 24 Hrs).\n" +
          "6. Redeem ExtraBucks Rewards at checkout on future purchases.";
        updatedOffer.offerType = "ExtraCare Rewards";
      } else if (offer.vendor === "Walgreens") {
        updatedOffer.instructions =
          "How to Claim Walgreens Cash Rewards:\n" +
          "1. Sign in to the Walgreens app or Walgreens.com with your myWalgreens account.\n" +
          "2. Go to the product page.\n" +
          "3. Clip/activate the Walgreens Cash offer.\n" +
          "4. Shop eligible items online or in-store and enter your myWalgreens number at checkout.\n" +
          "5. Walgreens Cash appears in your account within 24 hours of pickup/delivery.\n" +
          "6. Redeem Walgreens Cash on future purchases at checkout.";
        updatedOffer.offerType = "Walgreens Cash";
      } else if (offer.vendor === "Staples") {
        updatedOffer.instructions =
          "How to Claim Staples Easy Rewards:\n" +
          "1. Join Rewards – Sign up free at Staples.com/rewards or in-store.\n" +
          "2. Activate the Offer – Go to your Staples Rewards account → Offers tab → Click Activate.\n" +
          "3. Shop the Eligible Item.\n" +
          "4. Wait for Rewards – Staples Rewards around the 15th of the following month.\n" +
          "5. Redeem Rewards – Use your earned rewards like cash on future purchases (excludes gift cards, postage, etc.).\n" +
          "6. Use Before Expiry – Rewards expire in 2 months, so don’t wait too long!";
        updatedOffer.offerType = "Easy Rewards";
      } else if (offer.vendor === "CVS" || offer.vendor === "CVS Pharmacy") {
        updatedOffer.instructions =
          "How to Earn CVS ExtraCare Rewards:\n" +
          "1. Sign in to the CVS app or CVS.com with your ExtraCare account.\n" +
          "2. Go to the offer or product page.\n" +
          "3. Clip/activate the ExtraCare offer before making a purchase.\n" +
          "4. Shop eligible items online or in-store and scan your ExtraCare card or provide your phone number at checkout.\n" +
          "5. ExtraBucks Rewards will appear in your account usually within 1–2 days after purchase.\n" +
          "6. Redeem ExtraBucks Rewards on future CVS purchases at checkout.";
        updatedOffer.offerType = "ExtraCare Rewards";
      } else if (offer.vendor === "Office Depot") {
        updatedOffer.instructions =
          "How to Claim Office Depot Rewards:\n" +
          "1. Join Rewards – Sign up free at officedepot.com/rewards or in-store.\n" +
          "2. Shop the Eligible Item – Make sure you are logged in or provide your phone number at checkout.\n" +
          "3. Earn Rewards – Earn 2% back in rewards on qualifying purchases (bonus offers may apply).\n" +
          "4. Wait for Rewards – Rewards are issued monthly once you earn at least $3.\n" +
          "5. Redeem Rewards – Use rewards like store credit online or in-store on future purchases.\n" +
          "6. Use Before Expiry – Rewards usually expire about 60 days after they are issued.";
        updatedOffer.offerType = "Office Depot Rewards";
      } else {
        updatedOffer.instructions = "";
      }
    } else if (name === "cashback" && (!value || Number(value) === 0)) {
      updatedOffer.instructions = "";
    }

    // --- NEW: Auto-update end date when offerType changes ---
    if (name === "offerType") {
      updatedOffer.offerType = value;

      const vendorName = updatedOffer.vendor?.trim()?.toLowerCase();
      let rule = null;

      // 🧩 STEP 1: Try offerType + vendor match
      if (vendorName) {
        rule = offerTypeRules.find(
          (r) =>
            r.offerType === value &&
            r.vendor &&
            r.vendor.toLowerCase() === vendorName
        );
      }

      // 🧩 STEP 2: If no vendor match, look for all rules with same offerType
      if (!rule) {
        const sameTypeRules = offerTypeRules.filter((r) => r.offerType === value);

        if (sameTypeRules.length === 1) {
          // Only one match → safe to use
          rule = sameTypeRules[0];
        } else if (sameTypeRules.length > 1) {
          // Multiple matches → try to find a generic (no vendor) rule
          const genericRule = sameTypeRules.find((r) => !r.vendor);
          if (genericRule) {
            rule = genericRule;
          } else {
            // Otherwise pick the one with earliest endDttm
            rule = sameTypeRules.reduce((earliest, current) => {
              const getDate = (r) => {
                if (r.endDttm?.toDate) return r.endDttm.toDate();
                if (typeof r.endDttm === "string") return new Date(r.endDttm);
                return new Date();
              };
              return getDate(current) < getDate(earliest) ? current : earliest;
            });
          }
        }
      }

      // 🧩 STEP 3: Apply rule if found
      if (rule) {
        const endDate = new Date();

        // --- CASE 1: offset defined (including 0) ---
        if (rule.offset !== undefined && rule.offset !== null) {
          endDate.setDate(endDate.getDate() + Number(rule.offset));

          const year = endDate.getFullYear();
          const month = String(endDate.getMonth() + 1).padStart(2, "0");
          const day = String(endDate.getDate()).padStart(2, "0");
          updatedOffer.endDttm = `${year}-${month}-${day}`;
        }

        // --- CASE 2: Fixed end date (Timestamp or string) ---
        else if (rule.endDttm) {
          let endDateStr = "";

          if (typeof rule.endDttm === "object" && typeof rule.endDttm.toDate === "function") {
            const d = rule.endDttm.toDate();
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, "0");
            const day = String(d.getDate()).padStart(2, "0");
            endDateStr = `${year}-${month}-${day}`;
          } else if (typeof rule.endDttm === "string" && /^\d{4}-\d{2}-\d{2}$/.test(rule.endDttm)) {
            endDateStr = rule.endDttm;
          }

          updatedOffer.endDttm = endDateStr;
        }

        // --- CASE 3: No offset or end date ---
        else {
          updatedOffer.endDttm = "";
        }
      } else {
        updatedOffer.endDttm = "";
      }
    }


    if (name === "promoCode" && value.trim() !== "") {
      const cachedPromos = Cache.getData().promoCodes || [];
      const matchedPromo = cachedPromos.find(
        (p) => p.promoCode.toUpperCase() === value.toUpperCase()
      );

      const toInputDate = (date) => {
        if (!date) return "";
        const d = date.toDate ? date.toDate() : new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      if (matchedPromo) {
        updatedOffer.endDttm = toInputDate(matchedPromo.endDttm);
      }
    }

    if (name === "offerPrice") {
      if (updatedOffer.vendor === "Walmart") {
        if (parseFloat(value) >= 35) {
          updatedOffer.freeShippingMinimum = 0;
        } else {
          // reset if price drops below 35
          updatedOffer.freeShippingMinimum = 35;
        }
      }
       if (updatedOffer.vendor === "Amazon") {
        if (parseFloat(value) >= 35) {
          updatedOffer.freeShippingMinimum = 0;
        } else {
          // reset if price drops below 35
          updatedOffer.freeShippingMinimum = 35;
        }
      }
      if (updatedOffer.vendor === "Best Buy") {
        if (parseFloat(value) >= 35) {
          updatedOffer.freeShippingMinimum = 0;
        } else {
          // reset if price drops below 35
          updatedOffer.freeShippingMinimum = "";
        }
      }
      if (updatedOffer.vendor === "Staples") {
        if (parseFloat(value) >= 75) {
          updatedOffer.freeShippingMinimum = 0;
        } else {
          // reset if price drops below 75
          updatedOffer.freeShippingMinimum = "";
        }
      }
    }

    if (name === "offerLink") {
      if (updatedOffer.vendor === "Walmart") {
        const walmartRegex = /(https:\/\/www\.walmart\.com\/ip\/[^/]+\/\d+)/;
        const match = value.match(walmartRegex);
        if (match) {
          updatedOffer.offerLink = match[1];
        } else {
          updatedOffer.offerLink = value; // fallback
        }
      }
      if (updatedOffer.vendor === "Target") {
        updatedOffer.offerLink = value.split('#')[0];
      }
    }


    if (name === "productName" || name === "description") {
      const textToCheck = (updatedOffer.productName + " " + updatedOffer.description).toLowerCase().trim();

      if (!textToCheck) {
        // Reset category if both fields are empty
        updatedOffer.category = "";
      } else {

        let matched = false;

        for (const rule of comboRules) {
          const allFound = rule.keywords.every(k => textToCheck.includes(k.toLowerCase()));
          if (allFound) {
            updatedOffer.category = rule.category;
            matched = true;
            break; // Stop at first combo match
          }
        }

        // 🔹 2️⃣ Fallback: single keyword map (your existing logic)
        if (!matched) {
          for (const [keyword, category] of Object.entries(keywordCategoryMap)) {
            if (textToCheck.includes(keyword.toLowerCase())) {
              updatedOffer.category = category;
              break;
            }
          }
        }
      }
    }


    setOffer(updatedOffer);
  };


  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setStatus("");
  };

  const validateOffer = (o, isFileUpload = false) => {
    if (!o.vendor || !o.productName || !o.startDttm || !o.endDttm) {
      return "Vendor, Product Name, Start Date, and End Date are required.";
    }

    if (isFileUpload) {
      if (o.offerPrice === "" || o.offerPrice === undefined) {
        return "Offer Price is required in file uploads.";
      }
      if (isNaN(o.offerPrice)) {
        return "Offer price must be a valid number.";
      }
    } else {
      if (o.offerPrice && isNaN(o.offerPrice)) {
        return "Offer price must be a valid number if provided.";
      }
    }

    if (o.originalPrice && isNaN(o.originalPrice)) {
      return "Original price must be a valid number if provided.";
    }


    if (
      o.offerPrice !== "" && o.offerPrice != null && !isNaN(o.offerPrice) &&
      o.originalPrice !== "" && o.originalPrice != null && !isNaN(o.originalPrice) &&
      Number(o.offerPrice) >= Number(o.originalPrice)
    ) {
      return "Offer price must be lower than the original price.";
    }


    const [year, month, day] = o.startDttm.split("-").map(Number);
    const startDate = new Date(year, month - 1, day);
    const [endYear, endMonth, endDay] = o.endDttm.split("-").map(Number);
    const endDate = new Date(endYear, endMonth - 1, endDay);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (endDate < startDate) return "End date cannot be before start date.";
    if (endDate < today) return "Expiry date cannot be in the past.";

    return null;
  };

  const cleanOfferObject = (obj) => {
    const cleaned = {};
    for (const [key, value] of Object.entries(obj)) {
      const isEmptyString = typeof value === "string" && value.trim() === "";
      const isInvalidNumber = typeof value === "number" && isNaN(value);
      const isEmptyArray = Array.isArray(value) && value.length === 0;

      if (
        value === undefined ||
        value === null ||
        isEmptyString ||
        isInvalidNumber ||
        isEmptyArray
      ) {
        continue; // skip
      }

      cleaned[key] = value;
    }
    return cleaned;
  };


  const handleSubmit = async () => {
    setStatus("");
    if (!file && !offer.vendor.trim()) {
      setStatus("Please fill the form or upload a JSON file.");
      return;
    }
    if (file && offer.vendor.trim()) {
      setStatus("Please either fill the form or upload a JSON file, not both.");
      return;
    }

    setLoading(true);

    if (file) {
      if (file.type !== "application/json") {
        setStatus("Please upload a valid JSON file.");
        setLoading(false);
        return;
      }

      try {
        const text = await file.text();
        const data = JSON.parse(text);
        if (!Array.isArray(data)) {
          setStatus("JSON must be an array of offer objects.");
          setLoading(false);
          return;
        }

        let addedOffers = [];
        let skippedOffers = [];
        let updatedOffers = [];
        let failedOffers = [];

        for (const o of data) {
          const validationError = validateOffer(o, true);
          if (validationError) {
            failedOffers.push(o.productName || "Unknown Product");
            continue;
          }

          try {
            const parsedStart = new Date(o.startDttm);
            parsedStart.setHours(0, 0, 0, 0);
            const parsedEnd = new Date(o.endDttm);
            parsedEnd.setHours(23, 59, 59, 999);

            const rawOffer = {
              vendor: o.vendor.trim(),
              productName: o.productName.trim(),
              description: o.description?.trim() || "",
              category: o.category || "",
              offerImage: o.offerImage || "",
              offerLink: o.offerLink || "",
              originalPrice: parseFloat(o.originalPrice),
              offerPrice: parseFloat(o.offerPrice),
              ...(o.rebateAmount !== undefined && o.rebateAmount !== "" && !isNaN(o.rebateAmount)
                ? { rebateAmount: parseFloat(o.rebateAmount) }
                : {}),
              ...(o.cashback !== undefined && o.cashback !== "" && !isNaN(o.cashback)
                ? { cashback: parseFloat(o.cashback) }
                : {}),
              productSKU: o.productSKU || "",
              productUPC: o.productUPC || "",
              instructions: o.instructions?.trim() || "",
              ...(o.freeShippingMinimum !== undefined && o.freeShippingMinimum !== "" && !isNaN(o.freeShippingMinimum)
                ? { freeShippingMinimum: parseFloat(o.freeShippingMinimum) }
                : {}),
              freeStorePickup: o.freeStorePickup || "No",
              offerType: o.offerType || "",
              tags: Array.isArray(o.tags)
                ? o.tags.map((tag) => String(tag).trim()).filter((tag) => tag !== "")
                : typeof o.tags === "string"
                  ? o.tags.split(",").map((tag) => tag.trim()).filter((tag) => tag !== "")
                  : [],
              promoCode: o.promoCode?.trim() || null,
              startDttm: Timestamp.fromDate(parsedStart),
              endDttm: Timestamp.fromDate(parsedEnd),
              uploadedDttm: Timestamp.now(),
            };

            const result = await addProductOffer(cleanOfferObject(rawOffer));

            if (result === "added") {
              addedOffers.push(o.productName);
            } else if (result === "skipped") {
              skippedOffers.push(o.productName);
            } else if (result === "updated") {
              updatedOffers.push(o.productName);
            } else {
              failedOffers.push(o.productName);
            }
          } catch (err) {
            failedOffers.push(o.productName || "Unknown Product");
          }
        }

        let statusMsg = `Bulk upload complete:\n`;

        if (addedOffers.length)
          statusMsg += `Offers added (${addedOffers.length}):\n- ${addedOffers.join("\n- ")}\n`;

        if (skippedOffers.length)
          statusMsg += `Offers skipped (${skippedOffers.length}):\n- ${skippedOffers.join("\n- ")}\n`;

        if (updatedOffers.length)
          statusMsg += `Offers updated (${updatedOffers.length}):\n- ${updatedOffers.join("\n- ")}\n`;

        if (failedOffers.length)
          statusMsg += `Offers failed (${failedOffers.length}):\n- ${failedOffers.join("\n- ")}\n`;

        setStatus(statusMsg);
      } catch (err) {
        setStatus("Failed to parse JSON file: " + err.message);
      }
      setFile(null);
    } else {
      const validationError = validateOffer(offer);
      if (validationError) {
        setStatus("❌ " + validationError);
        setLoading(false);
        return;
      }

      try {


        const [sYear, sMonth, sDay] = offer.startDttm.split("-").map(Number);
        const parsedStart = new Date(sYear, sMonth - 1, sDay, 0, 0, 0, 0);

        const [eYear, eMonth, eDay] = offer.endDttm.split("-").map(Number);
        const parsedEnd = new Date(eYear, eMonth - 1, eDay, 23, 59, 59, 999);

        const rawOffer = {
          ...offer,
          ...(offer.originalPrice && !isNaN(offer.originalPrice)
            ? { originalPrice: parseFloat(offer.originalPrice) }
            : {}),
          offerPrice: parseFloat(offer.offerPrice),
          ...(offer.rebateAmount !== "" && !isNaN(offer.rebateAmount)
            ? { rebateAmount: parseFloat(offer.rebateAmount) }
            : {}),
          ...(offer.cashback !== "" && !isNaN(offer.cashback)
            ? { cashback: parseFloat(offer.cashback) }
            : {}),
          ...(offer.freeShippingMinimum !== "" && !isNaN(offer.freeShippingMinimum)
            ? { freeShippingMinimum: parseFloat(offer.freeShippingMinimum) }
            : {}),
          tags: offer.tags.split(",").map((tag) => tag.trim()).filter((tag) => tag !== ""),
          startDttm: Timestamp.fromDate(parsedStart),
          endDttm: Timestamp.fromDate(parsedEnd),
          uploadedDttm: Timestamp.now(),
        };

        const success = await addProductOffer(cleanOfferObject(rawOffer));


        if (success === 'added') {
          setOffer(initialState);
          setSelectedVendor(null);
          setStatus("✅ Offer uploaded successfully!");
        } else if (success === 'skipped') {
          setStatus("⚠️ Offer skipped: duplicate offer detected.");
        } else if (success === 'updated') {
          setOffer(initialState);
          setSelectedVendor(null);
          setStatus("✏️ Offer updated with new price.");
        } else {
          setStatus("❌ Failed to upload offer.");
        }

      } catch (err) {
        console.error("Error uploading offer:", err);
        setStatus("❌ Failed to upload offer.");
      }
    }

    setLoading(false);
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-md max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">📤 Upload New Product Offer</h2>
        <button
          onClick={handleSubmit}
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded shadow"
        >
          {loading ? "Uploading..." : "Upload Product Offer"}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* 1. Vendor Select */}
        <div className="w-full">
          <Select
            options={vendorOptions}
            value={selectedVendor}
            onChange={(option) => {
              setSelectedVendor(option);

              let updatedOffer = { ...offer, vendor: option ? option.value : "" };

              const today = new Date();
              const pad = (n) => n.toString().padStart(2, "0");

              if (option) {
                if (option.value === "Amazon") {
                  const amazonExpiry = new Date();
                  amazonExpiry.setDate(today.getDate() + 3);
                  updatedOffer.offerType = "Limited time deal";
                  //updatedOffer.endDttm = `${amazonExpiry.getFullYear()}-${pad(amazonExpiry.getMonth() + 1)}-${pad(amazonExpiry.getDate())}`;
                  updatedOffer.endDttm = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
                  updatedOffer.freeStorePickup = "No"; // default for Amazon
                } else if (option.value === "Best Buy") {
                  updatedOffer.endDttm = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
                  updatedOffer.freeStorePickup = "Yes";
                  updatedOffer.offerType = "Deal Of The Day"; // default
                } else if (option.value === "Lowe's") {
                  updatedOffer.endDttm = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
                  updatedOffer.freeStorePickup = "Yes";
                  updatedOffer.offerType = "Deal Of The Day"; // default
                } else if (option.value === "Walmart") {
                  const walmartExpiry = new Date();
                  walmartExpiry.setDate(today.getDate() + 6);
                  updatedOffer.endDttm = `${walmartExpiry.getFullYear()}-${pad(walmartExpiry.getMonth() + 1)}-${pad(walmartExpiry.getDate())}`;
                  updatedOffer.freeStorePickup = "Yes"; // default for Walmart
                  updatedOffer.offerType = "Clearance"; // default
                  // Clean Walmart link if already entered
                  if (updatedOffer.offerLink) {
                    const walmartRegex = /(https:\/\/www\.walmart\.com\/ip\/[^/]+\/\d+)/;
                    const match = updatedOffer.offerLink.match(walmartRegex);
                    if (match) {
                      updatedOffer.offerLink = match[1];
                    }
                  }
                } else if (option.value === "Ebay") {
                  const ebayExpiry = new Date();
                  ebayExpiry.setDate(today.getDate() + 6);
                  updatedOffer.endDttm = `${ebayExpiry.getFullYear()}-${pad(ebayExpiry.getMonth() + 1)}-${pad(ebayExpiry.getDate())}`;
                  updatedOffer.freeShippingMinimum = 0;
                  // updatedOffer.promoCode = "ADIDAS40";
                } else if (option.value === "Macy's") {
                  const ebayExpiry = new Date();
                  ebayExpiry.setDate(today.getDate() + 6);
                  updatedOffer.endDttm = `${ebayExpiry.getFullYear()}-${pad(ebayExpiry.getMonth() + 1)}-${pad(ebayExpiry.getDate())}`;
                  updatedOffer.freeShippingMinimum = 0;
                  updatedOffer.freeStorePickup = "Yes";
                  updatedOffer.offerType = "Sale";
                } else if (option.value === "Gapfactory") {
                  updatedOffer.freeStorePickup = "No"; // default for GapFactory
                  updatedOffer.freeShippingMinimum = 50;
                } else {
                  // Reset fields for other vendors
                  updatedOffer.offerType = "";
                  updatedOffer.endDttm = "";
                  updatedOffer.freeStorePickup = "No";
                  updatedOffer.freeShippingMinimum = "";
                }
              } else {
                // Vendor cleared
                updatedOffer.offerType = "";
                updatedOffer.endDttm = "";
                updatedOffer.freeStorePickup = "No";
              }

              setOffer(updatedOffer);
            }}
            isClearable
            isDisabled={loading || !!file}
            placeholder="Select Vendor"
            filterOption={(option, input) =>
              option.label.toLowerCase().replace(/['"\s]/g, "").includes(
                input.toLowerCase().replace(/['"\s]/g, "")
              )
            }
          />

        </div>

        {/* 2. Product Name */}
        <input
          name="productName"
          value={offer.productName}
          onChange={handleChange}
          placeholder="Product Name"
          className="border p-2 rounded"
          disabled={loading || !!file}
        />

        {/* 3. Description */}
        <input
          name="description"
          value={offer.description}
          onChange={handleChange}
          placeholder="Description"
          className="border p-2 rounded"
          disabled={loading || !!file}
        />

        {/* 4. Category */}
        <select
          name="category"
          value={offer.category}
          onChange={handleChange}
          className="border p-2 rounded w-full"
          disabled={loading || !!file}
        >
          <option value="">Select Category</option>
          {categories.map((cat, idx) => (
            <option key={idx} value={cat}>
              {cat}
            </option>
          ))}
        </select>

        {/* 5. Offer Link */}
        <input
          name="offerLink"
          value={offer.offerLink}
          onChange={handleChange}
          placeholder="Offer Link"
          className="border p-2 rounded"
          disabled={loading || !!file}
        />

        {/* 6. Offer Image URL */}
        <input
          name="offerImage"
          value={offer.offerImage}
          onChange={handleChange}
          placeholder="Offer Image URL"
          className="border p-2 rounded"
          disabled={loading || !!file}
        />

        {/* Offer Image Preview */}
        {offer.offerImage && (
          <img
            src={offer.offerImage}
            alt="Offer Preview"
            className="max-h-20 rounded shadow col-span-full object-contain"
          />
        )}

        {/* 7. Original Price */}
        <input
          name="originalPrice"
          value={offer.originalPrice}
          onChange={handleChange}
          placeholder="Original Price"
          type="number"
          className="border p-2 rounded"
          disabled={loading || !!file}
        />

        {/* 8. Offer Price */}
        <input
          name="offerPrice"
          value={offer.offerPrice}
          onChange={handleChange}
          placeholder="Offer Price"
          type="number"
          className="border p-2 rounded"
          disabled={loading || !!file}
        />

        {/* 9. Offer Type */}
        <select
          name="offerType"
          value={offer.offerType || ""}
          onChange={handleChange}
          className="border p-2 rounded"
          disabled={loading || !!file}
        >
          {/* Blank option */}
          <option value="">Select Offer Type</option>

          {/* Unique Offer Types */}
          {[...new Set(offerTypeRules.map((rule) => rule.offerType))].map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>


        {/* Start & Expiry Dates in one row */}
        <div className="col-span-full flex gap-4">
          {/* Start Date */}
          <div className="flex flex-col flex-1">
            <label className="text-sm font-medium">Start Date:</label>
            <input
              name="startDttm"
              type="date"
              value={offer.startDttm}
              onChange={handleChange}
              className="border p-2 rounded"
              disabled={loading || !!file}
            />
          </div>

          {/* Expiry Date */}
          <div className="flex flex-col flex-1">
            <label className="text-sm font-medium">Expiry Date:</label>
            <input
              name="endDttm"
              type="date"
              value={offer.endDttm}
              onChange={handleChange}
              className="border p-2 rounded"
              disabled={loading || !!file}
            />
          </div>
        </div>

        {/* 12. Free Store Pickup */}
        <div className="col-span-full">
          <label className="block mb-1 font-medium">Free Store Pickup:</label>
          <select
            name="freeStorePickup"
            value={offer.freeStorePickup}
            onChange={handleChange}
            className="border p-2 rounded w-full"
            disabled={loading || !!file}
          >
            <option value="No">No</option>
            <option value="Yes">Yes</option>
          </select>
        </div>

        {/* 13. Free Shipping Minimum */}
        <input
          name="freeShippingMinimum"
          value={offer.freeShippingMinimum}
          onChange={handleChange}
          placeholder="Free Shipping Minimum"
          type="number"
          className="border p-2 rounded"
          disabled={loading || !!file}
        />

        {/* 14. Promo Code */}
        <input
          name="promoCode"
          value={offer.promoCode}
          onChange={handleChange}
          placeholder="Promo Code"
          className="border p-2 rounded"
          disabled={loading || !!file}
        />

        {/* 15. Rebate Amount */}
        <input
          name="rebateAmount"
          value={offer.rebateAmount}
          onChange={handleChange}
          placeholder="Rebate Amount"
          type="number"
          className="border p-2 rounded"
          disabled={loading || !!file}
        />

        {/* 16. Cashback */}
        <input
          name="cashback"
          value={offer.cashback}
          onChange={handleChange}
          placeholder="Cashback"
          type="number"
          className="border p-2 rounded"
          disabled={loading || !!file}
        />

        {/* 17. Product SKU */}
        <input
          name="productSKU"
          value={offer.productSKU}
          onChange={handleChange}
          placeholder="Product SKU"
          className="border p-2 rounded"
          disabled={loading || !!file}
        />

        {/* 18. Product UPC */}
        <input
          name="productUPC"
          value={offer.productUPC}
          onChange={handleChange}
          placeholder="Product UPC"
          className="border p-2 rounded"
          disabled={loading || !!file}
        />

        {/* 19. Instructions */}
        <textarea
          name="instructions"
          value={offer.instructions}
          onChange={handleChange}
          placeholder="Instructions (e.g., redemption steps)"
          rows={4}
          className="border p-2 rounded col-span-full"
          disabled={loading || !!file}
        />

        {/* 20. Tags */}
        <textarea
          name="tags"
          value={offer.tags}
          onChange={handleChange}
          placeholder="Tags (comma-separated)"
          rows={3}
          className="border p-2 rounded col-span-full"
          disabled={loading || !!file}
        />
      </div>


      {/* OR separator */}
      <div className="text-center font-semibold my-4">OR</div>

      {/* File upload */}
      <div className="mb-4 text-center">
        <label className="flex flex-col items-center">
          Upload JSON File (Array of Product Offers):
          <input
            type="file"
            accept=".json,application/json"
            onChange={handleFileChange}
            disabled={loading}
          />
          {file && <p className="mt-1 text-sm text-gray-600">{file.name}</p>}
        </label>
      </div>


      {status && (
        <p
          className={`mt-2 text-sm text-center ${status.startsWith("❌") ? "text-red-600" : "text-green-600"
            }`}
          style={{ whiteSpace: "pre-line" }}
        >
          {status}
        </p>
      )}
    </div>
  );
}
